import {
  useMyAccountQuery,
  usePlaybookSetByIdQuery,
  useSubscribeToPlaybookSetMutation,
  useUnsubscribeFromPlaybookSetMutation,
} from "@qw/graphql";
import { PlaybookType } from "@qw/qw-common";
import { Badge, Button, Column, ColumnContent, Icon, Spinner } from "@qw/ui";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { PlaybookListItem } from "./playbook-list-item";

export const PlaybookSetDetail = () => {
  const navigate = useNavigate();
  const { setId } = useParams<{ setId: string }>();

  const { data: meData } = useMyAccountQuery();
  const { data, loading, error } = usePlaybookSetByIdQuery({
    variables: { id: setId! },
    skip: !setId,
  });

  const [subscribeToSet, { loading: isSubscribing }] = useSubscribeToPlaybookSetMutation();
  const [unsubscribeFromSet, { loading: isUnsubscribing }] = useUnsubscribeFromPlaybookSetMutation();

  const playbookSet = data?.playbookSet;

  const playbooks = useMemo(() => {
    // WorkflowStep playbooks are internal and Template playbooks are fill
    // targets. Hide both from set browsing for the same reason we hide them from
    // the standalone discover list.
    return (playbookSet?.playbooks ?? []).filter(
      (p) =>
        ((p.type as PlaybookType | undefined) ?? PlaybookType.Workflow) !== PlaybookType.WorkflowStep &&
        ((p.type as PlaybookType | undefined) ?? PlaybookType.Workflow) !== PlaybookType.Template
    );
  }, [playbookSet]);

  const handleSubscribeToSet = async () => {
    if (!setId) {
      return;
    }
    await subscribeToSet({
      variables: { id: setId },
      refetchQueries: ["PlaybookSetById", "PlaybookSets", "MySubscribedPlaybooks", "MyInvokablePlaybooks"],
    });
  };

  const handleUnsubscribeFromSet = async () => {
    if (!setId) {
      return;
    }
    await unsubscribeFromSet({
      variables: { id: setId },
      refetchQueries: ["PlaybookSetById", "PlaybookSets", "MySubscribedPlaybooks", "MyInvokablePlaybooks"],
    });
  };

  if (!setId) {
    return (
      <Column className="flex-auto bg-background">
        <ColumnContent className="p-4">
          <div className="text-center text-muted-foreground">Invalid set ID</div>
        </ColumnContent>
      </Column>
    );
  }

  if (loading) {
    return (
      <Column className="flex-auto bg-background">
        <ColumnContent className="p-4 flex items-center justify-center">
          <Spinner className="size-6" />
        </ColumnContent>
      </Column>
    );
  }

  if (error || !playbookSet) {
    return (
      <Column className="flex-auto bg-background">
        <ColumnContent className="p-4">
          <div className="text-center text-muted-foreground">Error loading playbook set</div>
        </ColumnContent>
      </Column>
    );
  }

  return (
    <Column className="flex-auto bg-background">
      <ColumnContent className="p-4">
        <div className="mb-6">
          <div className="flex justify-between">
            <div className="flex items-start gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate("/playbooks/discover")}>
                <Icon icon="arrow_back" className="mr-1" />
              </Button>
              <div className="flex flex-col">
                <h1 className="text-2xl font-medium text-accent-foreground mb-0">{playbookSet.name}</h1>
                {playbookSet.description && <p className="text-muted-foreground text-sm">{playbookSet.description}</p>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {playbookSet.isSubscribed ? (
                <Button variant="outline" size="sm" onClick={handleUnsubscribeFromSet} disabled={isUnsubscribing}>
                  <Icon icon="check" className="mr-1" />
                  Subscribed
                </Button>
              ) : (
                <Button variant="default" size="sm" onClick={handleSubscribeToSet} disabled={isSubscribing}>
                  Subscribe to Set
                </Button>
              )}
              <Badge variant="secondary" size="xs" className="text-2xs gap-1">
                {playbookSet.playbookCount} Playbook{playbookSet.playbookCount !== 1 ? "s" : ""}
              </Badge>
            </div>
          </div>
        </div>

        {playbooks.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No playbooks in this set yet</div>
        ) : (
          <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(340px,1fr))]">
            {playbooks.map((p) => (
              <PlaybookListItem
                key={p.id}
                title={p.name || "(untitled)"}
                description={p.description ?? undefined}
                author={`${p.author?.firstName ?? ""} ${p.author?.lastName ?? ""}`.trim()}
                type={(p.type as PlaybookType | undefined) ?? PlaybookType.Workflow}
                subscribers={p.subscribers ?? []}
                subscribed={p.subscribers?.some((s) => s.id === meData?.myAccount?.id)}
                onClick={() => navigate(`/playbooks/${p.id}`, { state: { fromDiscover: true } })}
              />
            ))}
          </div>
        )}
      </ColumnContent>
    </Column>
  );
};
